/**
 * In-app notification sound.
 *
 * Design decisions (see the P1 notification remediation):
 *  - Synthesized with the Web Audio API instead of an audio file: no asset to
 *    ship, no network fetch, and the tone is deterministic. This is a real
 *    implementation, not a placeholder audio element.
 *  - Only a small, deliberate set of events is audible. Everything else stays
 *    silent so the sound never becomes noise.
 *  - The sound is strictly event-driven: it is played only by the realtime
 *    handler for a notification that was just received. Hydrating the list from
 *    the API never plays anything, and the caller dedupes by notification id, so
 *    a refetch, a reconnect, or a repeated socket event cannot replay history.
 *  - Browser autoplay policy: an AudioContext starts suspended until the user
 *    has interacted with the page. We resume it on the first user gesture and,
 *    if it is still blocked when an event arrives, we play nothing — the visual
 *    unread badge remains the source of truth. We never claim sound played when
 *    the browser refused.
 *  - The user can turn it off; the preference lives in localStorage.
 */

/** Events that legitimately warrant immediate audible attention. */
const AUDIBLE_TYPES = new Set([
  'payment_completed',
  'doctor_assigned',
  'booking_declined',
  'message_received',
  'emergency_alert',
]);

const STORAGE_KEY = 'bhealth_notification_sound';

let ctx: AudioContext | null = null;
let unlocked = false;
let gestureBound = false;

/** True when this notification type should make a sound at all. */
export function isAudibleNotificationType(type?: string | null): boolean {
  return !!type && AUDIBLE_TYPES.has(type);
}

/** User preference (default: on). */
export function isNotificationSoundEnabled(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === null ? true : raw === 'true';
  } catch {
    return true;
  }
}

export function setNotificationSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
  } catch {
    /* preference is best-effort */
  }
}

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor = window.AudioContext || (window as any).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) {
    try {
      ctx = new Ctor();
    } catch {
      ctx = null;
    }
  }
  return ctx;
}

/**
 * Ensure the audio context is running. Must be called from a user gesture
 * (click/keydown) — that is the only moment a browser allows audio to start.
 */
export function unlockNotificationSound(): void {
  const c = getContext();
  if (!c) return;
  if (c.state === 'suspended') {
    c.resume().then(() => { unlocked = c.state === 'running'; }).catch(() => {});
  } else {
    unlocked = c.state === 'running';
  }
}

/** Attach one-shot gesture listeners so audio becomes available after any interaction. */
export function primeNotificationSoundOnFirstGesture(): () => void {
  if (typeof window === 'undefined' || gestureBound) return () => {};
  gestureBound = true;
  const handler = () => {
    unlockNotificationSound();
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('keydown', handler);
  };
  window.addEventListener('pointerdown', handler, { once: true });
  window.addEventListener('keydown', handler, { once: true });
  return () => {
    gestureBound = false;
    window.removeEventListener('pointerdown', handler);
    window.removeEventListener('keydown', handler);
  };
}

/**
 * Play the notification chime. Returns true when a tone was actually started.
 * Safe to call anywhere: never throws, silently does nothing if audio is
 * unavailable or still blocked by autoplay policy.
 */
export function playNotificationSound(): boolean {
  if (!isNotificationSoundEnabled()) return false;
  const c = getContext();
  if (!c || c.state !== 'running') return false;

  try {
    const now = c.currentTime;
    const gain = c.createGain();
    gain.connect(c.destination);
    // Short, soft two-note chime: C6 then E6.
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.16, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);

    [1046.5, 1318.5].forEach((freq, i) => {
      const osc = c.createOscillator();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.11);
      osc.connect(gain);
      osc.start(now + i * 0.11);
      osc.stop(now + i * 0.11 + 0.28);
    });
    return true;
  } catch {
    return false;
  }
}
